import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, ApiError, type Me, setAccessToken, setUnauthorizedHandler } from '../api/client';
import { config } from '../config';

interface AuthState {
  status: 'loading' | 'anonymous' | 'authenticated';
  me: Me | null;
  /** Mode démonstration : adopte le jeton obtenu par la connexion identifiant / mot de passe. */
  acceptDevToken(accessToken: string): Promise<void>;
  /** Connexion par NIP : session CASIJUD remise par l'API après le retour du service Identité & NIP. */
  acceptNipSession(accessToken: string): Promise<void>;
  loginOidc(): Promise<void>;
  completeOidc(): Promise<void>;
  logout(): Promise<void>;
  refreshMe(): Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

// En mode dev, le jeton (de démonstration) tient le temps de l'onglet. Jamais de localStorage.
const DEV_TOKEN_KEY = 'casijud.dev.token';
// Session ouverte par la connexion NIP : même règle (onglet seulement).
const NIP_TOKEN_KEY = 'casijud.nip.token';

const oidc =
  config.authMode === 'oidc'
    ? new UserManager({
        authority: config.oidc.authority,
        client_id: config.oidc.clientId,
        redirect_uri: `${window.location.origin}/connexion/retour`,
        post_logout_redirect_uri: window.location.origin,
        response_type: 'code',
        scope: 'openid profile',
        userStore: new WebStorageStateStore({ store: window.sessionStorage }),
      })
    : null;

function readStored(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function store(key: string, value: string) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // stockage indisponible : la session durera le temps de la page
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthState['status']>('loading');
  const [me, setMe] = useState<Me | null>(null);

  const clear = useCallback(() => {
    setAccessToken(null);
    try {
      window.sessionStorage.removeItem(DEV_TOKEN_KEY);
      window.sessionStorage.removeItem(NIP_TOKEN_KEY);
    } catch {
      // stockage indisponible
    }
    setMe(null);
    setStatus('anonymous');
  }, []);

  const loadMe = useCallback(async () => {
    try {
      setMe(await api<Me>('/moi'));
      setStatus('authenticated');
    } catch (err) {
      // Seule une session refusée (401) déconnecte : un refus temporaire (429) ou une coupure réseau ne doit pas
      // faire perdre la session ; on réessaie une fois après une courte pause.
      if (err instanceof ApiError && err.status === 401) {
        clear();
        return;
      }
      await new Promise((r) => setTimeout(r, 1500));
      try {
        setMe(await api<Me>('/moi'));
        setStatus('authenticated');
      } catch (again) {
        if (again instanceof ApiError && again.status === 401) clear();
        else setStatus((s) => (s === 'loading' ? 'anonymous' : s));
      }
    }
  }, [clear]);

  useEffect(() => {
    setUnauthorizedHandler(clear);
    (async () => {
      const token =
        readStored(NIP_TOKEN_KEY) ??
        (oidc ? (await oidc.getUser())?.access_token : readStored(DEV_TOKEN_KEY));
      if (token) {
        setAccessToken(token);
        await loadMe();
      } else {
        setStatus('anonymous');
      }
    })();
    return () => setUnauthorizedHandler(null);
  }, [clear, loadMe]);

  const value = useMemo<AuthState>(
    () => ({
      status,
      me,
      acceptDevToken: async (accessToken) => {
        setAccessToken(accessToken);
        store(DEV_TOKEN_KEY, accessToken);
        await loadMe();
      },
      acceptNipSession: async (accessToken) => {
        setAccessToken(accessToken);
        store(NIP_TOKEN_KEY, accessToken);
        await loadMe();
      },
      loginOidc: async () => {
        await oidc?.signinRedirect();
      },
      completeOidc: async () => {
        const user = await oidc!.signinRedirectCallback();
        setAccessToken(user.access_token);
        await loadMe();
      },
      logout: async () => {
        const nipSession = readStored(NIP_TOKEN_KEY) !== null;
        clear();
        if (oidc && !nipSession) await oidc.signoutRedirect();
      },
      refreshMe: loadMe,
    }),
    [status, me, loadMe, clear],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth hors de AuthProvider');
  return ctx;
}
